import { CanvasEngine, CanvasEngineEntity } from '..'
import { isApp } from '../../..'
import { getCurrentColor } from '../../../app/constant'
import { useDefectPhotoStore } from '../../../app/data/Editor/defectPhoto'
import { Defect } from '../../../app/data/types'
import { useCanvasStore } from '../../../app/store/canvas'
import { useWebEditorDataStore } from '../../../app/store/webeditor'
import { isPreviousShape } from '../../../app/util'

//엔진체크
function getEngine(): CanvasEngine {
  const canvasStore = useCanvasStore()
  const canvasEngine = canvasStore.getCanvasEngine()
  if (!canvasEngine) {
    throw new Error('CanvasEngine is not initialized')
  }
  return canvasEngine
}
//id 기준으로 특정 엔티티들을 한번에 삭제하고 리로드 하는 함수
function deleteMultipleEntities(entityIds: string[]): void {
  const canvasEngine = getEngine()
  for (const entityId of entityIds) {
    const entityExists = canvasEngine?.getEntityById(entityId)
    if (entityExists?.isSelected) {
      canvasEngine.selectEntityById('')
    }
    if (entityExists) {
      entityExists.node.destroy()
    }
  }
  canvasEngine.blurSelectedEntity()
  removeEntitiesByIds(entityIds)
}

//인자로 들어온 entityId를 제외한 this.entities를 다시 소팅하는 함수
function removeEntitiesByIds(entityIds: string[]): void {
  const canvasEngine = getEngine()
  canvasEngine.removeEntityByIds(entityIds)
}

//엔티티 설정해야하는게 너무 많아서 분리
function applyDefectShapes(shape: Defect, entity: CanvasEngineEntity | undefined): void {
  if (!entity) return
  entity?.setBalloonFontColor(
    shape.shape_data.balloonFontColor ? shape.shape_data.balloonFontColor : shape.color,
  )
  entity?.setBalloonFillColor(
    shape.shape_data.balloonFillColor ? shape.shape_data.balloonFillColor : '#fff',
  )
  entity?.setBalloonScale(shape.shape_data.balloonScale)
  entity?.setConnectedLineThicknessScale(shape.shape_data.connectedLineThicknessScale)
  entity?.setBalloonFontScale(shape.shape_data.balloonFontScale)
  entity?.setPointShapeScale(shape.shape_data.pointShapeScale)
  entity?.setAreaBorderThicknessScale(shape.shape_data.areaBorderThicknessScale)
  if (entity && entity.isTripod() && shape.shape_data.tripodShapePoints?.tailStyle) {
    entity.setTailStyle(shape.shape_data.tripodShapePoints.tailStyle)
  }
}
//shape와 index로 특정엔티티를 만들어주는 함수 : 배열임
function createMultipleEntities(shapes: Defect[], indexes: number[]): void {
  const canvasEngine = getEngine()
  const currentEntities: CanvasEngineEntity[] = []
  for (let i = 0; i < shapes.length; i++) {
    const shape = shapes[i]
    const index = indexes[i]
    const currentShapeId = isApp() ? (shape as Defect).localStringId : shape.id.toString()
    if (!currentShapeId) continue
    // 무도형(NO_SHAPE) 결함은 캔버스 엔티티가 없다 — 삭제 undo 복원 시 import 시도하지 않음
    if (!shape.shape_data) continue
    canvasEngine.importEntityToCurrentIndex(
      {
        ...shape.shape_data,
        shapeId: currentShapeId,
        color: shape.color,
        layerId: 'default-layer',
      },
      index,
    )
    const entity = canvasEngine.getEntityById(currentShapeId)
    if (!entity) continue
    currentEntities.push(entity as CanvasEngineEntity)
    entity?.changeColor(shape.color)
    applyDefectShapes(shape, entity)
    if (shape.shape_data?.isFill) {
      entity?.setShapeFillToggle(true)
    }
    if (shape.shape_data?.rectType) {
      entity?.setShapeStyleToggle(shape.shape_data.rectType)
    }
    if (shape.shape_data?.dotType) {
      entity?.setIndividualDotTypeSetting(shape.shape_data.dotType)
      entity?.setDotStyleToggle(shape.shape_data.dotType)
    }

    if (isPreviousShape(shape.previous_defect_id) && shape.previous_balloon_number) {
      entity?.setBalloonAdminText(shape.previous_balloon_number)
    }
    if (isPreviousShape(shape.previous_defect_id) && shape.progressing == 1) {
      entity?.setBalloonCircle(true)
      entity?.enableIndex()
    }
    // 전차 항목이면서 진행중인 경우, 동그라미로 표시
    else if (isPreviousShape(shape.previous_defect_id) && shape.progressing != 1) {
      entity?.setBalloonCircle(true)
      let isPreviousHasDefectPhoto = false
      if (isApp()) {
        const defectPhotoStore = useDefectPhotoStore()
        isPreviousHasDefectPhoto = defectPhotoStore.getIsPreviousHasDefectPhoto(currentShapeId)
      } else {
        const dataStore = useWebEditorDataStore()
        isPreviousHasDefectPhoto = dataStore.getIsPreviousHasDefectPhoto(currentShapeId)
      }
      if (isPreviousHasDefectPhoto || !!shape.is_current) {
        entity?.enableIndex()
      } else {
        entity?.changeColor(getCurrentColor('purple') ?? shape.color)
      }
    } else {
      entity?.enableIndex()
    }
  }
}

//단일 항목에 대한 캐시 업데이트 함수
function updateEntityCache(entityId: string): void {
  const canvasEngine = getEngine()
  if (!canvasEngine) return
  const entity = canvasEngine.getEntityById(entityId)
  if (!entity) return
  if (canvasEngine.isEntityStatic(entity)) {
    canvasEngine.unmarkEntityStatic(entity)
  }
  entityCache(entity)
}

function refreshCache(): void {
  const canvasEngine = getEngine()
  if (!canvasEngine) return
  canvasEngine.refreshCache()
}

async function refreshAsyncCache(): Promise<void> {
  const canvasEngine = getEngine()
  if (!canvasEngine) return
  await canvasEngine.refreshAsyncCache()
}

async function restoreTargetCache(entities: CanvasEngineEntity[]): Promise<void> {
  const canvasEngine = getEngine()
  if (!canvasEngine) return
  await canvasEngine.restoreTargetCache(entities)
}

function entityCache(entity: CanvasEngineEntity): void {
  const canvasEngine = getEngine()
  if (!canvasEngine) return
  // listening(false) 상태에서 구운 캐시는 hit 캔버스가 비어(stale) 선택 불가가 되므로 스킵
  // — uncached 상태는 live hit으로 동작하고, 재캐싱은 배치 캐싱이 처리
  if (!entity.node.listening()) return
  // 브러시는 캐시 제외 (엔진 캐시 정책과 동일 — 대형 비트맵·stale 비트맵 방지)
  if (entity.isBrush()) return
  canvasEngine.markEntityStatic(entity)
}

const UTIL = {
  deleteMultipleEntities,
  removeEntitiesByIds,
  createMultipleEntities,
  updateEntityCache,
  refreshCache,
  entityCache,
  refreshAsyncCache,
  restoreTargetCache,
}
export default UTIL
